import { useCallback, useEffect, useState } from 'react';
import {
  type AskedQuestion,
  type AskedQuestionQueue,
  byPresentationOrder,
  MAX_ANSWER_LENGTH,
  MAX_REJECTION_REASON_LENGTH,
  type ServerEvent,
} from '@floorpoll/shared';
import { FormError } from '../../components/Form.js';
import { ApiError } from '../../lib/api.js';
import { quizApi } from '../../lib/quizApi.js';

/**
 * The console's Q&A surface (#118) — moderating what the room asked, and
 * putting one of it on the projector.
 *
 * One panel rather than the issue's separate moderator tab and presenter
 * sidebar. Both are console UI at the same trust level, both are reached from
 * the presenter page, and in a small team they are frequently the same person
 * at the same laptop — two surfaces would mean two sockets and two copies of
 * this state to keep in step. The columns are the moderator's job; the
 * "Put on screen" and answer controls are the presenter's.
 */

const EMPTY_QUEUE: AskedQuestionQueue = { pending: [], approved: [], rejected: [] };

/** Applies one moderated question to whichever column it now belongs in. */
function withQuestion(queue: AskedQuestionQueue, question: AskedQuestion): AskedQuestionQueue {
  const without = (list: AskedQuestion[]) =>
    list.filter((q) => q.askedQuestionId !== question.askedQuestionId);

  const next: AskedQuestionQueue = {
    pending: without(queue.pending),
    approved: without(queue.approved),
    rejected: without(queue.rejected),
  };
  next[question.status] = [...next[question.status], question];
  next.approved = [...next.approved].sort(byPresentationOrder);
  return next;
}

function withoutQuestion(queue: AskedQuestionQueue, askedQuestionId: string): AskedQuestionQueue {
  const without = (list: AskedQuestion[]) =>
    list.filter((q) => q.askedQuestionId !== askedQuestionId);
  return {
    pending: without(queue.pending),
    approved: without(queue.approved),
    rejected: without(queue.rejected),
  };
}

interface Props {
  quizId: string;
  /** Whether the room may ask right now — the flag this panel toggles. */
  qandaOpen: boolean;
  onToggleOpen: (open: boolean) => void;
  busy: boolean;
  /**
   * Registers this panel's event handler with the page that owns the socket.
   *
   * The presenter page holds one `useLiveQuiz`, and a second socket here would
   * double every push the room generates. Handing the handler up is what keeps
   * one connection serving both.
   */
  onRegisterHandler: (handler: (event: ServerEvent) => void) => void;
  /**
   * Registers this panel's re-read with the page that owns the socket.
   *
   * A dropped socket is recovered by `useLiveQuiz` calling `onResync`, not by
   * any event — nothing is broadcast when a connection comes back. Without
   * this the moderator would return from a Wi-Fi blip to a Waiting column
   * that had silently stopped filling.
   */
  onRegisterReload: (reload: () => void) => void;
}

export function QandaPanel({
  quizId,
  qandaOpen,
  onToggleOpen,
  busy,
  onRegisterHandler,
  onRegisterReload,
}: Props) {
  const [queue, setQueue] = useState<AskedQuestionQueue>(EMPTY_QUEUE);
  const [presented, setPresented] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  // Which question's answer box is open, and what is in it. One at a time: the
  // presenter is answering the question that is up, not drafting several.
  const [answering, setAnswering] = useState<string | null>(null);
  const [answerDraft, setAnswerDraft] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await quizApi.questions(quizId);
      setQueue(result.queue);
      setPresented(result.presentedAskedQuestionId);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load the questions.');
    }
  }, [quizId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The socket's own recovery path. `state` below covers a fresh run; this
  // covers a reconnect, which broadcasts nothing at all.
  useEffect(() => {
    onRegisterReload(() => void load());
  }, [onRegisterReload, load]);

  // Every Q&A push, including the two only a presenter view receives. Handed
  // up rather than subscribed here — see onRegisterHandler.
  useEffect(() => {
    onRegisterHandler((event: ServerEvent) => {
      switch (event.type) {
        case 'state':
          // Broadcast when a run starts, and a new run's questions are not the
          // last one's. A reconnect is a different signal entirely and arrives
          // through onRegisterReload above, because nothing is broadcast for it.
          void load();
          break;
        case 'questionAsked':
          // Pending, so it lands in the left column. This is the push no phone
          // and no projector receives.
          setQueue((current) =>
            withQuestion(current, { ...event.question, status: 'pending' as const }),
          );
          break;
        case 'questionModerated':
          // A colleague moderated it. The approval carries the question; a
          // rejection carries only the id, so the row is re-read rather than
          // guessed at.
          if (event.question) {
            setQueue((current) => withQuestion(current, event.question as AskedQuestion));
          } else {
            void load();
          }
          break;
        case 'questionLiked':
          setQueue((current) => ({
            pending: current.pending,
            approved: current.approved.map((q) =>
              q.askedQuestionId === event.askedQuestionId ? { ...q, likes: event.likes } : q,
            ),
            rejected: current.rejected,
          }));
          break;
        case 'questionAnswered':
          setQueue((current) => ({
            ...current,
            approved: current.approved.map((q) =>
              q.askedQuestionId === event.askedQuestionId
                ? { ...q, answer: event.answer, answeredAt: event.answeredAt }
                : q,
            ),
          }));
          break;
        case 'questionPresented':
          setPresented(event.askedQuestionId);
          break;
        case 'questionDeleted':
          setQueue((current) => withoutQuestion(current, event.askedQuestionId));
          break;
        default:
          // Screens, votes and controls belong to the page above, not here.
          break;
      }
    });
  }, [onRegisterHandler, load]);

  /**
   * Runs one moderation action against a row.
   *
   * `working` is the row's id rather than a boolean, so only the buttons on
   * the row being acted on go quiet — a moderator working through a queue
   * should not have the whole panel lock on every click.
   */
  async function act(askedQuestionId: string, action: () => Promise<void>) {
    setWorking(askedQuestionId);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That didn't work. Try again.");
      // The row may have moved under us — a colleague approving what this
      // laptop was rejecting. Re-read rather than leaving a stale column up.
      await load();
    } finally {
      setWorking(null);
    }
  }

  const approve = (question: AskedQuestion) =>
    act(question.askedQuestionId, async () => {
      const updated = await quizApi.approveQuestion(quizId, question.askedQuestionId);
      setQueue((current) => withQuestion(current, updated));
    });

  const reject = (question: AskedQuestion) =>
    act(question.askedQuestionId, async () => {
      // A prompt rather than an inline form: rejecting is the rarer action and
      // a reason is optional, so a field sitting on every pending row would be
      // clutter on the column the moderator spends their time in.
      const reason = prompt(
        `Why is this not going forward? (optional, ${MAX_REJECTION_REASON_LENGTH} characters)\n\nOnly the person who asked will see it.`,
        '',
      );
      // Cancel means "changed my mind", not "reject with no reason" — an empty
      // string from OK is the second of those and still rejects.
      if (reason === null) return;
      const updated = await quizApi.rejectQuestion(
        quizId,
        question.askedQuestionId,
        reason.trim() || undefined,
      );
      setQueue((current) => withQuestion(current, updated));
    });

  const remove = (question: AskedQuestion) =>
    act(question.askedQuestionId, async () => {
      if (!confirm('Delete this question for good? This cannot be undone.')) return;
      await quizApi.deleteQuestion(quizId, question.askedQuestionId);
      setQueue((current) => withoutQuestion(current, question.askedQuestionId));
      if (presented === question.askedQuestionId) setPresented(null);
    });

  const present = (askedQuestionId: string | null) =>
    act(askedQuestionId ?? 'none', async () => {
      setPresented(await quizApi.presentQuestion(quizId, askedQuestionId));
    });

  /**
   * Steps a question one place through the running order.
   *
   * Buttons rather than the drag-to-reorder the issue describes: the same
   * outcome, reachable from a keyboard and workable on a touchscreen, and the
   * pattern the poll editor already uses for moving questions.
   *
   * Both neighbours are renumbered from their positions in the reordered list,
   * so an approved question that never had an `order` acquires one the first
   * time anybody moves anything — which is what stops "unordered sorts last"
   * fighting the moderator's arrangement.
   */
  const move = (question: AskedQuestion, direction: -1 | 1) => {
    const ordered = [...queue.approved].sort(byPresentationOrder);
    const index = ordered.findIndex((q) => q.askedQuestionId === question.askedQuestionId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ordered.length) return;

    const swapped = [...ordered];
    const [moved] = swapped.splice(index, 1);
    if (!moved) return;
    swapped.splice(target, 0, moved);

    return act(question.askedQuestionId, async () => {
      // Only the two that actually changed places, not the whole column: a
      // full renumber would be N requests to move one row.
      const lower = Math.min(index, target);
      const upper = Math.max(index, target);
      for (let position = lower; position <= upper; position += 1) {
        const entry = swapped[position];
        if (!entry) continue;
        const updated = await quizApi.approveQuestion(quizId, entry.askedQuestionId, position);
        setQueue((current) => withQuestion(current, updated));
      }
    });
  };

  const submitAnswer = (question: AskedQuestion) =>
    act(question.askedQuestionId, async () => {
      const text = answerDraft.trim();
      if (text.length === 0) return;
      const updated = await quizApi.answerQuestion(quizId, question.askedQuestionId, text);
      setQueue((current) => withQuestion(current, updated));
      setAnswering(null);
      setAnswerDraft('');
    });

  const ordered = [...queue.approved].sort(byPresentationOrder);

  return (
    <div className="panel qanda-panel">
      <div className="qanda-head">
        <h2>
          Questions from the room
          {queue.pending.length > 0 ? (
            <span className="qanda-badge" aria-label={`${queue.pending.length} waiting`}>
              {queue.pending.length}
            </span>
          ) : null}
        </h2>
        <button
          type="button"
          className="secondary"
          disabled={busy}
          aria-pressed={qandaOpen}
          onClick={() => onToggleOpen(!qandaOpen)}
        >
          {qandaOpen ? 'Stop taking questions' : 'Take questions'}
        </button>
      </div>

      <FormError message={error} />

      {!qandaOpen ? (
        <p className="muted">
          The room cannot ask anything yet. Turning this on puts an ask box on every phone that has
          joined.
        </p>
      ) : null}

      <div className="qanda-columns">
        <section className="qanda-column">
          <h3>Waiting ({queue.pending.length})</h3>
          {queue.pending.length === 0 ? (
            <p className="muted">Nothing waiting.</p>
          ) : (
            <ul>
              {queue.pending.map((question) => (
                <li key={question.askedQuestionId} className="qanda-row">
                  <p className="qanda-text">{question.content}</p>
                  <div className="qanda-row-actions">
                    <button
                      type="button"
                      disabled={working === question.askedQuestionId}
                      onClick={() => void approve(question)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      disabled={working === question.askedQuestionId}
                      onClick={() => void reject(question)}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={working === question.askedQuestionId}
                      onClick={() => void remove(question)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="qanda-column">
          <h3>Running order ({ordered.length})</h3>
          {ordered.length === 0 ? (
            <p className="muted">Nothing approved yet.</p>
          ) : (
            <ul>
              {ordered.map((question, index) => {
                const isPresented = question.askedQuestionId === presented;
                const rowBusy = working === question.askedQuestionId;
                return (
                  <li
                    key={question.askedQuestionId}
                    className={`qanda-row${isPresented ? ' presented' : ''}`}
                  >
                    <p className="qanda-text">{question.content}</p>
                    <p className="qanda-meta">
                      {question.likes} {question.likes === 1 ? 'like' : 'likes'}
                      {isPresented ? ' · on screen now' : ''}
                    </p>
                    {question.answer ? <p className="qanda-answer">{question.answer}</p> : null}

                    {answering === question.askedQuestionId ? (
                      <div className="qanda-answer-form">
                        <textarea
                          value={answerDraft}
                          onChange={(changeEvent) => setAnswerDraft(changeEvent.target.value)}
                          maxLength={MAX_ANSWER_LENGTH}
                          rows={3}
                          aria-label="Your answer"
                          placeholder="What did you say?"
                        />
                        <div className="qanda-row-actions">
                          <button
                            type="button"
                            disabled={rowBusy || answerDraft.trim().length === 0}
                            onClick={() => void submitAnswer(question)}
                          >
                            Save answer
                          </button>
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => {
                              setAnswering(null);
                              setAnswerDraft('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="qanda-row-actions">
                        <button
                          type="button"
                          disabled={rowBusy}
                          onClick={() =>
                            void present(isPresented ? null : question.askedQuestionId)
                          }
                        >
                          {isPresented ? 'Take off screen' : 'Put on screen'}
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          disabled={rowBusy}
                          onClick={() => {
                            setAnswering(question.askedQuestionId);
                            setAnswerDraft(question.answer ?? '');
                          }}
                        >
                          {question.answer ? 'Edit answer' : 'Record answer'}
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          disabled={rowBusy || index === 0}
                          aria-label="Move earlier"
                          onClick={() => void move(question, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          disabled={rowBusy || index === ordered.length - 1}
                          aria-label="Move later"
                          onClick={() => void move(question, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="danger"
                          disabled={rowBusy}
                          onClick={() => void remove(question)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="qanda-column">
          <h3>Not going forward ({queue.rejected.length})</h3>
          {queue.rejected.length === 0 ? (
            <p className="muted">Nothing rejected.</p>
          ) : (
            <ul>
              {queue.rejected.map((question) => (
                <li key={question.askedQuestionId} className="qanda-row rejected">
                  <p className="qanda-text">{question.content}</p>
                  {question.rejectionReason ? (
                    <p className="qanda-meta">{question.rejectionReason}</p>
                  ) : null}
                  <div className="qanda-row-actions">
                    <button
                      type="button"
                      className="secondary"
                      disabled={working === question.askedQuestionId}
                      onClick={() => void approve(question)}
                    >
                      Approve after all
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={working === question.askedQuestionId}
                      onClick={() => void remove(question)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
